import React, { useState } from 'react';
import { Code2, Mail, Phone, Globe, Clock, Tag as TagIcon, CheckCircle2, XCircle, Download } from 'lucide-react';

/** Renders a backed-up item as a readable preview instead of raw JSON. */

type AnyRecord = Record<string, unknown>;

const ISO_DATE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

function labelize(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function isIsoDate(v: unknown): v is string {
  return typeof v === 'string' && ISO_DATE.test(v);
}

function fmtValue(v: unknown): React.ReactNode {
  if (v === null || v === undefined || v === '') return <span className="text-ink-3">—</span>;
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (isIsoDate(v)) return new Date(v).toLocaleString();
  if (typeof v === 'string' && /^https?:\/\//.test(v)) {
    return <a href={v} target="_blank" rel="noreferrer" className="text-accent-ink hover:underline break-all">{v}</a>;
  }
  return String(v);
}

/** Pick readable text color for a chip background. */
function chipText(hex: string | undefined): string {
  if (!hex || !/^#[0-9a-fA-F]{6}$/.test(hex)) return '#0B1F3A';
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) > 150 ? '#0B1F3A' : '#FFFFFF';
}

function TagChip({ name, color, title }: { name: string; color?: string; title?: string }) {
  const bg = color && /^#[0-9a-fA-F]{6}$/.test(color) ? color : '#EEEEEE';
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[12px] font-semibold"
      style={{ backgroundColor: bg, color: chipText(bg) }}
      title={title}
    >
      <TagIcon className="w-3 h-3" />
      {name}
    </span>
  );
}

function FieldGrid({ fields }: { fields: Array<[string, unknown]> }) {
  if (!fields.length) return null;
  return (
    <div className="grid gap-x-8 gap-y-2 sm:grid-cols-2">
      {fields.map(([k, v]) => (
        <div key={k} className="flex items-baseline justify-between gap-4 border-b border-line-2 py-1.5">
          <span className="text-[12.5px] font-semibold text-ink-3 whitespace-nowrap">{labelize(k)}</span>
          <span className="text-[13.5px] text-ink text-right break-all">{fmtValue(v)}</span>
        </div>
      ))}
    </div>
  );
}

function downloadJson(data: unknown, filename: string) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function RawJsonToggle({ data, filename }: { data: unknown; filename?: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-4">
      <div className="flex items-center gap-5">
        <button
          onClick={() => setOpen(!open)}
          className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-ink-3 hover:text-ink"
        >
          <Code2 className="w-3.5 h-3.5" />
          {open ? 'Hide raw JSON' : 'View raw JSON'}
        </button>
        <button
          onClick={() => downloadJson(data, filename || 'item.json')}
          className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-ink-3 hover:text-ink"
          title="Download this item as a JSON file"
        >
          <Download className="w-3.5 h-3.5" />
          Download JSON
        </button>
      </div>
      {open && (
        <pre className="mt-2 bg-[var(--code-bg,#06101D)] text-[var(--code-ink,#cfe7fb)] border border-line rounded-[11px] p-4 font-mono text-xs overflow-x-auto max-h-96">
          {JSON.stringify(data, null, 2)}
        </pre>
      )}
    </div>
  );
}

/* ---------- Contacts ---------- */

interface CFContactTag { name?: string; color?: string; applied_at?: string }

function ContactPreview({ data, filename }: { data: AnyRecord; filename?: string }) {
  const first = (data.first_name as string) || '';
  const last = (data.last_name as string) || '';
  const name = `${first} ${last}`.trim() || (data.email_address as string) || 'Unnamed contact';
  const initials = (first[0] || (data.email_address as string)?.[0] || '?').toUpperCase() + (last[0] || '').toUpperCase();
  const tags = Array.isArray(data.tags) ? (data.tags as CFContactTag[]) : [];
  const attrs = (data.custom_attributes && typeof data.custom_attributes === 'object')
    ? Object.entries(data.custom_attributes as AnyRecord)
    : [];
  const isActive = data.is_active === true;
  const unsubscribed = data.unsubscribed_at != null;

  const socials: Array<[string, unknown]> = (['website_url', 'fb_url', 'twitter_url', 'instagram_url', 'linkedin_url'] as const)
    .filter((k) => data[k])
    .map((k) => [k, data[k]]);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-full bg-accent-soft flex items-center justify-center font-display font-bold text-accent-ink text-[15px] flex-none">
          {initials}
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-display font-bold text-[17px] text-ink">{name}</span>
            {isActive && !unsubscribed && (
              <span className="inline-flex items-center gap-1 text-[11.5px] font-bold text-success bg-success-soft px-2 py-0.5 rounded-full">
                <CheckCircle2 className="w-3 h-3" /> Active
              </span>
            )}
            {unsubscribed && (
              <span className="inline-flex items-center gap-1 text-[11.5px] font-bold text-danger bg-danger-soft px-2 py-0.5 rounded-full">
                <XCircle className="w-3 h-3" /> Unsubscribed {isIsoDate(data.unsubscribed_at) ? new Date(data.unsubscribed_at as string).toLocaleDateString() : ''}
              </span>
            )}
          </div>
          <div className="flex items-center gap-4 mt-0.5 text-[13px] text-ink-2 flex-wrap">
            {typeof data.email_address === 'string' && data.email_address && (
              <span className="inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-ink-3" />{data.email_address}</span>
            )}
            {typeof data.phone_number === 'string' && data.phone_number && (
              <span className="inline-flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-ink-3" />{data.phone_number}</span>
            )}
            {typeof data.time_zone === 'string' && data.time_zone && (
              <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-ink-3" />{data.time_zone}</span>
            )}
          </div>
        </div>
      </div>

      {/* Tags */}
      {tags.length > 0 && (
        <div>
          <div className="text-[11.5px] font-bold text-ink-3 uppercase tracking-wide mb-2">Tags ({tags.length})</div>
          <div className="flex flex-wrap gap-1.5">
            {tags.map((t, i) => (
              <TagChip
                key={i}
                name={t.name || 'unnamed'}
                color={t.color}
                title={t.applied_at ? `Applied ${new Date(t.applied_at).toLocaleDateString()}` : undefined}
              />
            ))}
          </div>
        </div>
      )}

      {/* Custom attributes */}
      {attrs.length > 0 && (
        <div>
          <div className="text-[11.5px] font-bold text-ink-3 uppercase tracking-wide mb-2">Custom attributes</div>
          <FieldGrid fields={attrs} />
        </div>
      )}

      {/* Socials + record info */}
      {socials.length > 0 && (
        <div>
          <div className="text-[11.5px] font-bold text-ink-3 uppercase tracking-wide mb-2 inline-flex items-center gap-1.5"><Globe className="w-3.5 h-3.5" />Links</div>
          <FieldGrid fields={socials} />
        </div>
      )}
      <div>
        <div className="text-[11.5px] font-bold text-ink-3 uppercase tracking-wide mb-2">Record</div>
        <FieldGrid fields={[
          ['id', data.id],
          ['public_id', data.public_id],
          ['created_at', data.created_at],
          ['updated_at', data.updated_at],
        ]} />
      </div>

      <RawJsonToggle data={data} filename={filename} />
    </div>
  );
}

/* ---------- Tags ---------- */

function TagPreview({ data, filename }: { data: AnyRecord; filename?: string }) {
  const color = typeof data.color === 'string' ? data.color : '#EEEEEE';
  return (
    <div className="space-y-5">
      <div className="flex items-center gap-4">
        <div
          className="w-12 h-12 rounded-[11px] border border-line flex-none"
          style={{ backgroundColor: /^#[0-9a-fA-F]{6}$/.test(color) ? color : '#EEEEEE' }}
          title={color}
        />
        <div>
          <div className="font-display font-bold text-[17px] text-ink">{String(data.name || 'Unnamed tag')}</div>
          <div className="text-[13px] text-ink-2 font-mono">{color}</div>
        </div>
        <TagChip name={String(data.name || 'unnamed')} color={color} />
      </div>
      <FieldGrid fields={[
        ['id', data.id],
        ['public_id', data.public_id],
        ['created_at', data.created_at],
        ['updated_at', data.updated_at],
      ]} />
      <RawJsonToggle data={data} filename={filename} />
    </div>
  );
}

/* ---------- Generic (products, orders, courses, everything else) ---------- */

function GenericPreview({ data, filename }: { data: AnyRecord; filename?: string }) {
  const scalars: Array<[string, unknown]> = [];
  const nested: Array<[string, unknown]> = [];
  for (const [k, v] of Object.entries(data)) {
    if (v !== null && typeof v === 'object') nested.push([k, v]);
    else scalars.push([k, v]);
  }
  return (
    <div className="space-y-5">
      <FieldGrid fields={scalars} />
      {nested.map(([k, v]) => (
        <details key={k} className="border border-line rounded-[11px] px-4 py-2.5">
          <summary className="text-[13px] font-semibold text-ink-2 cursor-pointer">
            {labelize(k)} {Array.isArray(v) ? `(${(v as unknown[]).length})` : ''}
          </summary>
          <pre className="mt-2 bg-[var(--code-bg,#06101D)] text-[var(--code-ink,#cfe7fb)] rounded-[9px] p-3 font-mono text-xs overflow-x-auto max-h-72">
            {JSON.stringify(v, null, 2)}
          </pre>
        </details>
      ))}
      <RawJsonToggle data={data} filename={filename} />
    </div>
  );
}

export default function SnapshotItemPreview({ resourceType, data, filename }: { resourceType: string; data: unknown; filename?: string }) {
  if (data === null || typeof data !== 'object' || Array.isArray(data)) {
    return (
      <pre className="bg-[var(--code-bg,#06101D)] text-[var(--code-ink,#cfe7fb)] border border-line rounded-[11px] p-4 font-mono text-xs overflow-x-auto max-h-96">
        {JSON.stringify(data, null, 2)}
      </pre>
    );
  }
  const record = data as AnyRecord;
  if (resourceType === 'contacts') return <ContactPreview data={record} filename={filename} />;
  if (resourceType === 'tags' || resourceType === 'contact_tags') return <TagPreview data={record} filename={filename} />;
  return <GenericPreview data={record} filename={filename} />;
}
